import React, { useState, useRef, useEffect } from "react";
import MaterialSelect from "@mui/material/Select";
import { Box, MenuItem, SelectChangeEvent, Select } from "@mui/material";
import { Divider } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import images from "../../../../Assets/images";
import VcDatePicker from "Components/UI/DatePicker/DatePicker";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import uparrowIcon from "../../../../Assets/images/tableHeadArrow.svg";
import moment from "moment";
import { scrollBar } from "Components/Common/Header/ManageOutsideClickClose";
import "../../../../Assets/css/date-picker.css";
import { useApiCall } from "Hooks/useApiCall";
import { getLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import {
  listEquipmentRegistry,
  updateEquipmentRegistry,
} from "Network/Core/EquipmentMaster/equipmentRegistry";
import { GetEquipmentRegistryListParams } from "Network/Core/EquipmentMaster/types";
import {
  downloadEquipmentListReports,
  getEquipmentTypes,
} from "Network/Core/EquipmentMaster/equipment";
import ButtonComponent from "Components/UI/Button";
import { DownloadLoader } from "Components/UI/Loader";
import "./style.css";
import Pagination from "Components/UI/Pagination/Pagination";
import { useRolePermission } from "Hooks/useRolePermission";
import FileUploadModal from "Components/Common/Modal/FileuploaderModal/FileUploadModal";
import { getActivities, getFilteredCommonUtils } from "Network/Core/CommonUtils/commonUtils";
import { toFormattedDateString } from "../../../../Library/Utils";

const SEARCH_DEBOUNCE_MS = 400;

const ListOfEquipment: React.FC = () => {
  const { t } = useTranslation();
  const { call } = useApiCall();
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const site = getLocalStorage(ls_vars.PROJECT_SITE_ID);
  const inputRefS = useRef<HTMLDivElement>(null);
  const inputRefE = useRef<HTMLDivElement>(null);
  const [orderBy, setOrderBy] = useState<string>("id");
  const [statusIsASC, setStatusIsASC] = useState<boolean>(true);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [fullList, setFullList] = useState<any[]>([]);
  const [equipmentRegistryList, setEquipmentRegistryList] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [pageCount, setPageCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState<Date | null>(
    new Date(new Date().setHours(23, 59, 59, 999))
  );
  const [isUploadFileVisible, setIsUploadFileVisible] =
    useState<boolean>(false);
  const [companyOptions, setCompanyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [fuelTypeOptions, setFuelTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [typeOfUsageOptions, setTypeOfUsageOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [contactNamesOptions, setContactNamesOptions] = useState<
    { label: string; value: string }[]
  >([]);
  // Add new state variables for filtered data and selected values
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedEquipmentType, setSelectedEquipmentType] =
    useState<string>("");
  const [selectedContactName, setSelectedContactName] = useState<string>("");
  const { checkPermission } = useRolePermission();
  const canViewEquipmentRegistry = checkPermission(["register:equipment:view"]);
  const canCreateEquipmentRegistry = checkPermission([
    "register:equipment:create",
  ]);
  const canUpdateEquipmentRegistry = checkPermission([
    "register:equipment:update",
  ]);
  const canDeleteEquipmentRegistry = checkPermission([
    "register:equipment:delete",
  ]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, startDate, endDate, selectedCompany, selectedEquipmentType, selectedContactName]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleEquipmentTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedEquipmentType(value ?? "");
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedCompany(value ?? "");
  };

  const handleContactNameChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedContactName(value ?? "");
  };

  // Update clear filters function
  const defaultStartDate = () =>
    new Date(new Date().setDate(new Date().getDate() - 30));
  const defaultEndDate = () => new Date(new Date().setHours(23, 59, 59, 999));

  const handleClearFilter = () => {
    setSelectedCompany("");
    setSelectedEquipmentType("");
    setSelectedContactName("");
    setSearchValue("");
    setDebouncedSearch("");
    setStartDate(defaultStartDate());
    setEndDate(defaultEndDate());
    setPage(1);
  };

  const headerStyle = {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
  };

  const renderHeader = [
    { name: "Scheduled Arrival", key: "scheduledArrival", width: "10%" },
    { name: "Company", key: "company", width: "10%" },
    { name: "Contact Name", key: "contactName", width: "10%" },
    { name: "Equipment Type", key: "equipmentType", width: "10%" },
    { name: "Reg: Make", key: "regMake", width: "10%" },
    { name: "Reg: Model", key: "regModel", width: "10%" },
    { name: "Reg: Year", key: "regYear", width: "10%" },
  ];

  const fetchEquipmentTypes = async () => {
    call(
      () => getEquipmentTypes(region, project.ref, site),
      (res: any) => {
        const options = res
          ?.map((item: any) => ({
            label: item.name,
            value: item.id,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
        setEquipmentTypeOptions(options);
      }
    );
  };

  const getFuelTypes = async () => {
    call(
      () => getFilteredCommonUtils("FUEL_TYPE", region),
      (data) => {
        const options = data
          ?.map((item: any) => ({
            label: item.name,
            value: item.id,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
        setFuelTypeOptions(options);
      }
    );
  };

  const getUsageTypes = async () => {
    call(
      () => getFilteredCommonUtils("EQUIPMENT_USAGE_TYPE", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        setTypeOfUsageOptions(options);
      }
    );
  };

  const fetchFilterDropdownSources = async () => {
    call(
      () =>
        listEquipmentRegistry(
          { page: 1, pageSize: 1000, search: "", orderBy: "id" } as GetEquipmentRegistryListParams,
          region,
          project.ref,
          site
        ),
      (res: any) => {
        const companies = (res.results ?? [])
          .map((e: { company: string }) => e.company)
          .reduce(
            (unique: { value: string; label: string }[], item: string) =>
              !item || unique.some((obj) => obj.label === item)
                ? unique
                : [...unique, { value: item, label: item }],
            []
          )
          .sort((a: any, b: any) => a.label.localeCompare(b.label));

        const contactNames = (res.results ?? [])
          .map((e: { contactName: string }) => e.contactName)
          .reduce(
            (unique: { value: string; label: string }[], item: string) =>
              !item || unique.some((obj) => obj.label === item)
                ? unique
                : [...unique, { value: item, label: item }],
            []
          )
          .sort((a: any, b: any) => a.label.localeCompare(b.label));

        setCompanyOptions(companies);
        setContactNamesOptions(contactNames);
      }
    );
  };

  const formatDate = (date: any) => {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  };

  const CLIENT_FETCH_PAGE_SIZE = 5000;

  const fetchEquipmentList = async () => {
    const params: GetEquipmentRegistryListParams = {
      page: 1,
      pageSize: CLIENT_FETCH_PAGE_SIZE,
      search: debouncedSearch,
      orderBy: orderBy,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    } as GetEquipmentRegistryListParams;

    call(
      () => listEquipmentRegistry(params, region, project.ref, site),
      (res: any) => {
        setFullList(res.results ?? []);
      }
    );
  };

  useEffect(() => {
    let filtered = fullList;

    if (selectedCompany) {
      filtered = filtered.filter(
        (item) =>
          item.company &&
          item.company.toString().toLowerCase() ===
            selectedCompany.toString().toLowerCase()
      );
    }

    if (selectedEquipmentType) {
      filtered = filtered.filter(
        (item) =>
          item.equipmentTypeId &&
          item.equipmentTypeId.toString() === selectedEquipmentType.toString()
      );
    }

    if (selectedContactName) {
      filtered = filtered.filter(
        (item) =>
          item.contactName &&
          item.contactName.toString().toLowerCase() ===
            selectedContactName.toString().toLowerCase()
      );
    }

    const newPageCount = Math.max(1, Math.ceil(filtered.length / perPage));
    setPageCount(newPageCount);

    const safePage = Math.min(page, newPageCount);
    if (safePage !== page) {
      setPage(safePage);
      return;
    }

    const start = (page - 1) * perPage;
    setEquipmentRegistryList(filtered.slice(start, start + perPage));
  }, [fullList, selectedCompany, selectedEquipmentType, selectedContactName, page, perPage]);

  const handleUpdateEquipmentRegistry = async (data: any) => {
    call(
      () => updateEquipmentRegistry(data.id, data, region, project.ref),
      (response) => {
        if (response) {
          // Conditional on purpose - the else branch below reports a failure,
          // so this can't move to the shared successMessage option.
          toast.success(t("Translation.equipment_registry_edit_success"));
          setEquipmentRegistryList((prevList) =>
            prevList.map((item) =>
              item.id === data.id
                ? { ...item, isActive: response.isActive }
                : item
            )
          );
        } else {
          console.error("Failed to create equipment registry");
        }
      }
    );
  };

  useEffect(() => {
    fetchEquipmentTypes();
    getFuelTypes();
    getUsageTypes();
    fetchFilterDropdownSources();
  }, []);

  useEffect(() => {
    fetchEquipmentList();
  }, [debouncedSearch, startDate, endDate, orderBy, isUploadFileVisible]);

  scrollBar(setIsScrolling);
  const toggleSort = (field: string, isAcs: boolean) => {
    if (field !== "id") {
      setStatusIsASC(!isAcs);
      setOrderBy(isAcs ? field : "-" + field);
    }
  };

  const handleUploadModalClose = () => {
    setIsUploadFileVisible(false);
  };

  const onChangePerPage = (e: { value: number }) => {
    setPerPage(e.value);
    setPage(1);
  };

  const handleDateChange = (date: Date | null, type: "start" | "end") => {
    if (type === "start") {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const handleActiveToggle = (data: any) => {
    handleUpdateEquipmentRegistry({ isActive: !data.isActive, id: data.id });
  };

  const exportReportData = async () => {
    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";

    call(
      () =>
        downloadEquipmentListReports(
          {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          },
          region,
          project.ref,
          site
        ),
      () => { }
    );
  };

  return (
    <>
      {isUploadFileVisible && (
        <FileUploadModal
          project={project.ref}
          region={region}
          handleClose={() => {
            handleUploadModalClose();
          }}
          type="listEquipment"
          additionalData={{
            fuelTypeOptions: fuelTypeOptions,
            equipmentTypeOptions: equipmentTypeOptions,
            typeOfUsageOptions: typeOfUsageOptions,
          }}
        />
      )}
      <div className="page-wrapper">
        <div className="page-header">
          <h3 className="page-title">{t("Translation.list_of_equipment")}</h3>
        </div>
        <div
          className={`page-content custom-list-div user-list-scroll scroll`}
        >
          <div className="booking-list-form">
            <Box
              sx={{
                padding: "0 24px 22px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              {/* First row: Filters with clear filter button */}
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  flexWrap: "wrap",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Select
                  disableUnderline
                  style={{
                    width: 250,
                    background: "#eff3f7",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                  variant="standard"
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {t("Translation.company")}
                  </MenuItem>
                  {companyOptions.map((item) => (
                    <MenuItem value={item.value} key={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  disableUnderline
                  style={{
                    width: 250,
                    background: "#eff3f7",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                  variant="standard"
                  value={selectedEquipmentType}
                  onChange={handleEquipmentTypeChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {t("Translation.equipment_type")}
                  </MenuItem>
                  {equipmentTypeOptions.map((item) => (
                    <MenuItem value={item.value} key={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  disableUnderline
                  style={{
                    width: 250,
                    background: "#eff3f7",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                  variant="standard"
                  value={selectedContactName}
                  onChange={handleContactNameChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {t("Translation.contact")} {t("Translation.name")}
                  </MenuItem>
                  {contactNamesOptions.map((item) => (
                    <MenuItem value={item.value} key={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>

                <button
                  className="btn white-btn filter-btn"
                  onClick={handleClearFilter}
                  style={{ margin: 0 }}
                >
                  <span>
                    {t("Translation.clear_filters")}
                  </span>
                </button>
              </Box>

              {/* Second row: Date filters and search on left, action buttons on right */}
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Box sx={{ 
                  display: "flex", 
                  gap: "16px", 
                  flexWrap: "wrap", 
                  alignItems: "center",
                  flex: "1 1 auto"
                }}>
                  <div>
                    <Box className="date-box-frontend">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          width: "100%",
                        }}
                      >
                        <VcDatePicker
                          date={startDate}
                          onDateChange={(val: Date | null) =>
                            handleDateChange(val, "start")
                          }
                        />
                      </Box>
                    </Box>
                  </div>
                  <Box className="to" sx={{ marginBottom: 0 }}>
                    {t("Translation.to")}
                  </Box>
                  <div>
                    <Box className="date-box-frontend">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          width: "100%",
                        }}
                      >
                        <VcDatePicker
                          date={endDate || new Date()}
                          onDateChange={(val) => handleDateChange(val, "end")}
                        />
                      </Box>
                    </Box>
                  </div>

                  <div className="form-group form-inline filter-search ml-0">
                    <img
                      src={images.search}
                      alt="search"
                      style={{
                        position: "relative",
                        left: "30px",
                        zIndex: 1,
                        paddingTop: "20px"
                      }}
                    />
                    <input
                      type="text"
                      className="form-control"
                      style={{
                        paddingLeft: "40px",
                        background: "#eff3f7",
                        borderRadius: "6px",
                        height: "40px",
                        marginTop: "20px"
                      }}
                      placeholder={t("Translation.search")}
                      value={searchValue}
                      onChange={handleSearchChange}
                    />
                  </div>
                </Box>
                
                <Box sx={{ 
                  display: "flex", 
                  gap: "10px", 
                  flexWrap: "nowrap", 
                  alignItems: "center",
                  justifyContent: "flex-end",
                  marginLeft: "auto"
                }}>
                  <ButtonComponent
                    className="btn white-btn dl-btn mr-3"
                    handleClick={() => { setIsUploadFileVisible(true); }}
                    buttonLabel={<img src={images.download} style={{ transform: "rotate(180deg)" }} />}
                  />
                  <ButtonComponent
                    className="btn white-btn dl-btn mr-3"
                    handleClick={exportReportData}
                    buttonLabel={<img src={images.download} />}
                  />
                  <ButtonComponent
                    className="btn white-btn dl-btn"
                    handleClick={() => { }}
                    buttonLabel={<PrintIcon style={{ width: 18, height: 18 }} />}
                  />
                </Box>
              </Box>
            </Box>
          </div>
          <Divider />

          <div className="booking-list-wrapper">
            <div
              className={`booking-list-data scroll ${isScrolling ? "show-scroll" : "no-scroll"
                }`}
              style={{ flex: "1 1 0%", overflow: "auto", padding: "0px 24px 24px" }}
            >
              <div className="table-responsive " style={{ overflowX: "auto" }}>
                <table className="display nowrap dataTable w-100">
                  <thead>
                    <tr>
                      <th></th>
                      <th
                        className="sorting sorting_asc"
                        onClick={() =>
                          toggleSort("scheduled_arrival", statusIsASC)
                        }
                      >
                        <span>{t("Translation.scheduled_arrivals")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() => toggleSort("company", statusIsASC)}
                      >
                        <span>{t("Translation.company")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() => toggleSort("contact_name", statusIsASC)}
                      >
                        <span>{`${t("Translation.contact")} ${t(
                          "Translation.name"
                        )}`}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() =>
                          toggleSort("equipment_type", statusIsASC)
                        }
                      >
                        <span> {t("Translation.equipment_type")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() =>
                          toggleSort("fuel_type__name", statusIsASC)
                        }
                      >
                        <span> {t("Translation.fuel_type")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() => toggleSort("reg_make", statusIsASC)}
                      >
                        <span>{t("Translation.regMake")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() => toggleSort("reg_model", statusIsASC)}
                      >
                        <span>{t("Translation.regModel")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() => toggleSort("reg_year", statusIsASC)}
                      >
                        <span>{t("Translation.regYear")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() => toggleSort("type_of_usage__name", statusIsASC)}
                      >
                        <span>{t("Translation.regUsage")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() =>
                          toggleSort("additional_notes", statusIsASC)
                        }
                      >
                        <span>{t("Translation.regOtherUsage")}</span>
                      </th>
                      <th
                        className="sorting"
                        onClick={() => toggleSort("ref", statusIsASC)}
                      >
                        <span>{t("Translation.reference")}</span>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {equipmentRegistryList.length > 0 ? (
                      equipmentRegistryList.map((row, index) => (
                        <tr key={row.id ?? index} className="odd ">
                          <td className="p-3" style={{ padding: "12px" }}>
                            <input
                              type="checkbox"
                              checked={row.isActive}
                              onChange={() => handleActiveToggle(row)}
                              disabled={!canUpdateEquipmentRegistry}
                            />
                          </td>
                          <td>{row.scheduledArrival && toFormattedDateString(row.scheduledArrival)}</td>
                          <td>{row.company}</td>
                          <td>{row.contactName}</td>
                          <td>{row.equipmentType}</td>
                          <td>{row.fuelTypeName}</td>
                          <td>{row.regMake}</td>
                          <td>{row.regModel}</td>
                          <td>{row.regYear}</td>
                          <td>{row.typeOfUsageName}</td>
                          <td>{row.additionalNotes}</td>
                          <td>{row.ref}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="odd">
                        <td className="td-no-found" colSpan={11}>
                          <h5>{t("Translation.no_data_found")}</h5>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {equipmentRegistryList.length > 0 ? (
                <Pagination
                  page={page}
                  onPageChange={(v) => setPage(v)}
                  perPage={perPage}
                  onPerPageChange={onChangePerPage}
                  pageTotalCount={pageCount}
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ListOfEquipment;
const styles = {
  headerStyle: {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
  },
  tableHeader: {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
    "& th": {
      padding: "12px",
    },
  },
  tableRow: {
    "& td": {
      padding: "12px",
    },
  },
};
